import type { SuiteEnumItem } from 'redpoints-model-js-common/enumeration/SuiteEnum';
import SuiteEnum from 'redpoints-model-js-common/enumeration/SuiteEnum';

import { BASE_URL } from 'redpoints-front-common-rp10/config';
import { ApplicationEnum } from 'redpoints-front-common-rp10/enumerations';
import {
  getEnvironment,
  getHttpSchemaWithFallback,
} from 'redpoints-front-common-rp10/helpers';

export const APPLICATION_NAME = ApplicationEnum.PORTAL;
export const APPLICATION_SUITE = (SuiteEnum.IPR
  .name as unknown) as SuiteEnumItem<'SuiteEnum'>;
export const APPLICATION_TOKEN = 'e0b6d2d2-2102-436f-8e7b-52f0f3aad808';
export const ENVIRONMENT = getEnvironment();
export const SCHEME = getHttpSchemaWithFallback();

export const VERSION = '[AIV]{version}[/AIV]';

const ROUTE_PATH_ACTORS = 'actors';
const ROUTE_PATH_DATABASE = 'database';
const ROUTE_PATH_NETWORKS = 'networks';
const ROUTE_PATH_VALIDATION = 'validation';
const ROUTE_PATH_RESULTS = 'results';
const ROUTE_PATH_ACCOUNT_DETAIL = 'account-detail';
const ROUTE_PATH_SELLER_DETAIL = 'seller-detail';

export const ROUTE_PATHS = {
  ACCOUNT_DETAIL: ROUTE_PATH_ACCOUNT_DETAIL,
  ACCOUNT_DETAILS_ACTORS: `${ROUTE_PATH_ACTORS}/${ROUTE_PATH_ACCOUNT_DETAIL}/:accountId`,
  ACCOUNT_DETAILS_DATABASE: `${ROUTE_PATH_DATABASE}/${ROUTE_PATH_ACCOUNT_DETAIL}/:accountId`,
  ACCOUNT_DETAILS_DATABASE_INCIDENT_DETAILS: `${ROUTE_PATH_DATABASE}/:incidentId/${ROUTE_PATH_ACCOUNT_DETAIL}/:accountId`,
  ACCOUNT_DETAILS_VALIDATION: `${ROUTE_PATH_VALIDATION}/${ROUTE_PATH_ACCOUNT_DETAIL}/:accountId`,
  ACCOUNT_DETAILS_VALIDATION_INCIDENT_DETAILS: `${ROUTE_PATH_VALIDATION}/:incidentId/${ROUTE_PATH_ACCOUNT_DETAIL}/:accountId`,
  ACTOR_NETWORKS: 'actor-networks',
  ACTOR_NETWORKS_ACTORS: `${ROUTE_PATH_ACTORS}/actor-networks/:actorId`,
  ACTOR_NETWORKS_NETWORKS: `${ROUTE_PATH_NETWORKS}/actor-networks/:actorId`,
  ACTORS: ROUTE_PATH_ACTORS,
  NETWORKS: ROUTE_PATH_NETWORKS,
  ANTIPIRACY_SUMMARY: `${ROUTE_PATH_RESULTS}/antipiracy`,
  BACKOFFICE_SEARCH_KEYWORDS_LIST_BY_ASSET: 'assets/search-keywords/all',
  TAGS_MANAGEMENT: 'tags-management',
  CUSTOM_REPORTS: `${ROUTE_PATH_RESULTS}/custom-reports`,
  DATABASE: ROUTE_PATH_DATABASE,
  DOCUMENT_CREATE: ':documentTypeName/create',
  DOCUMENTS_DETAILS: ':documentId/details',
  DOCUMENTS_EDITION: ':documentId/edition',
  DOCUMENTS: 'documents',
  DOMAIN_MANAGEMENT: 'domain-management',
  ECONOMIC_IMPACT: `${ROUTE_PATH_RESULTS}/economic-impact`,
  ENFORCEMENT_AUDIT: 'enforcement-audit',
  HOME: 'home',
  INCIDENT_DETAILS_DATABASE: `${ROUTE_PATH_DATABASE}/:incidentId`,
  INCIDENT_DETAILS_VALIDATION: `${ROUTE_PATH_VALIDATION}/:incidentId`,
  IP_OFFLINE_SERVICES: 'ip-offline-services',
  IPR_RULES_MANAGEMENT_INCIDENT: 'automation/rules-management-incident',
  JOURNEY: 'journey',
  MY_LINKS_AND_DOCS: `${ROUTE_PATH_RESULTS}/my-links-and-docs`,
  NEW_INCIDENT: 'new-incident',
  ASSET_VERIFICATION: 'asset-verification',
  ASSET_VERIFICATION_CARD_DETAIL: 'asset-verification/:detectionLinkId',
  INFRINGEMENT_EVALUATION: 'infringement-evaluation',
  NEW_SELLER_MANAGEMENT: 'new-seller-management',
  NON_ENFORCEABLE: `${ROUTE_PATH_RESULTS}/non-enforceable`,
  RESULTS: ROUTE_PATH_RESULTS,
  RESULT_ANALYSIS: `${ROUTE_PATH_RESULTS}/result-analysis`,
  REVENUE_RECOVERY: 'revenue-recovery',
  RULE_SUGGESTIONS: 'rule-suggestions',
  SELLERS: `${ROUTE_PATH_RESULTS}/sellers`,
  SELLER_DETAIL: ROUTE_PATH_SELLER_DETAIL,
  SELLER_DETAILS_ACTORS: `${ROUTE_PATH_ACTORS}/${ROUTE_PATH_SELLER_DETAIL}/:sellerId`,
  SELLER_DETAILS_DATABASE: `${ROUTE_PATH_DATABASE}/${ROUTE_PATH_SELLER_DETAIL}/:sellerId`,
  SELLER_DETAILS_DATABASE_INCIDENT_DETAILS: `${ROUTE_PATH_DATABASE}/:incidentId/${ROUTE_PATH_SELLER_DETAIL}/:sellerId`,
  SELLER_DETAILS_VALIDATION: `${ROUTE_PATH_VALIDATION}/${ROUTE_PATH_SELLER_DETAIL}/:sellerId`,
  SELLER_DETAILS_VALIDATION_INCIDENT_DETAILS: `${ROUTE_PATH_VALIDATION}/:incidentId/${ROUTE_PATH_SELLER_DETAIL}/:sellerId`,
  SELLER_GROUP_DETAILS_DATABASE: `${ROUTE_PATH_DATABASE}/seller-group-detail/:sellerGroupId`,
  SELLER_GROUP_DETAILS_VALIDATION: `${ROUTE_PATH_VALIDATION}/seller-group-detail/:sellerGroupId`,
  SELLER_MANAGEMENT: 'seller-management',
  SMART_ONBOARDING: 'smart-onboarding',
  SOCIAL_MEDIA_IMPACT: `${ROUTE_PATH_RESULTS}/social-media-impact`,
  SUCCESS_PLANS: 'success-plans',
  SUMMARY: `${ROUTE_PATH_RESULTS}/summary`,
  TEMPLATES: 'templates',
  TEMPLATES_CREATE: 'create',
  TEMPLATES_EDIT: 'edit/:templateId',
  VALIDATION: ROUTE_PATH_VALIDATION,
  WEBSITE_INFRINGEMENTS: `${ROUTE_PATH_RESULTS}/website-infringements`,
} as const;

export const DEFAULT_PAGE_PATH_ON_TENANT_CHANGE = ROUTE_PATHS.HOME;

export const PAGE_PATHS_TO_REDIRECT_ON_TENANT_CHANGE = {
  [ROUTE_PATHS.ACCOUNT_DETAILS_ACTORS]: ROUTE_PATHS.ACTORS,
  [ROUTE_PATHS.ACCOUNT_DETAILS_DATABASE]: ROUTE_PATHS.DATABASE,
  [ROUTE_PATHS.ACCOUNT_DETAILS_VALIDATION]: ROUTE_PATHS.VALIDATION,
  [ROUTE_PATHS.ACTOR_NETWORKS_ACTORS]: ROUTE_PATHS.ACTORS,
  [ROUTE_PATHS.ACTOR_NETWORKS_NETWORKS]: ROUTE_PATHS.ACTORS,
  [ROUTE_PATHS.INCIDENT_DETAILS_DATABASE]: ROUTE_PATHS.DATABASE,
  [ROUTE_PATHS.INCIDENT_DETAILS_VALIDATION]: ROUTE_PATHS.VALIDATION,
  [ROUTE_PATHS.SELLER_DETAILS_ACTORS]: ROUTE_PATHS.ACTORS,
  [ROUTE_PATHS.SELLER_DETAILS_DATABASE]: ROUTE_PATHS.DATABASE,
  [ROUTE_PATHS.SELLER_DETAILS_VALIDATION]: ROUTE_PATHS.VALIDATION,
  [ROUTE_PATHS.SELLER_GROUP_DETAILS_DATABASE]: ROUTE_PATHS.DATABASE,
  [ROUTE_PATHS.SELLER_GROUP_DETAILS_VALIDATION]: ROUTE_PATHS.VALIDATION,
};

export const ROUTES = {
  ACCOUNT_DETAIL: `${BASE_URL}/${ROUTE_PATHS.ACCOUNT_DETAIL}`,
  ACCOUNT_DETAILS_ACTORS: `${BASE_URL}/${ROUTE_PATHS.ACCOUNT_DETAILS_ACTORS}`,
  ACCOUNT_DETAILS_DATABASE: `${BASE_URL}/${ROUTE_PATHS.ACCOUNT_DETAILS_DATABASE}`,
  ACCOUNT_DETAILS_DATABASE_INCIDENT_DETAILS: `${BASE_URL}/${ROUTE_PATHS.ACCOUNT_DETAILS_DATABASE_INCIDENT_DETAILS}`,
  ACCOUNT_DETAILS_VALIDATION: `${BASE_URL}/${ROUTE_PATHS.ACCOUNT_DETAILS_VALIDATION}`,
  ACCOUNT_DETAILS_VALIDATION_INCIDENT_DETAILS: `${BASE_URL}/${ROUTE_PATHS.ACCOUNT_DETAILS_VALIDATION_INCIDENT_DETAILS}`,
  ACTOR_NETWORKS: `${BASE_URL}/${ROUTE_PATHS.ACTOR_NETWORKS}`,
  ACTOR_NETWORKS_ACTORS: `${BASE_URL}/${ROUTE_PATHS.ACTOR_NETWORKS_ACTORS}`,
  ACTOR_NETWORKS_NETWORKS: `${BASE_URL}/${ROUTE_PATHS.ACTOR_NETWORKS_NETWORKS}`,
  ACTOR_NETWORKS_DATABASE: `${BASE_URL}/${ROUTE_PATHS.DATABASE}/${ROUTE_PATHS.ACTOR_NETWORKS}/:actorId`,
  ACTOR_NETWORKS_DATABASE_ACCOUNT_DETAILS: `${BASE_URL}/${ROUTE_PATHS.DATABASE}/${ROUTE_PATHS.ACCOUNT_DETAIL}/:accountId/${ROUTE_PATHS.ACTOR_NETWORKS}/:actorId`,
  ACTOR_NETWORKS_DATABASE_INCIDENT_DETAILS_ACCOUNT_DETAILS: `${BASE_URL}/${ROUTE_PATHS.DATABASE}/:incidentId/${ROUTE_PATHS.ACCOUNT_DETAIL}/:accountId/${ROUTE_PATHS.ACTOR_NETWORKS}/:actorId`,
  ACTOR_NETWORKS_DATABASE_INCIDENT_DETAILS_SELLER_DETAILS: `${BASE_URL}/${ROUTE_PATHS.DATABASE}/:incidentId/${ROUTE_PATHS.SELLER_DETAIL}/:sellerId/${ROUTE_PATHS.ACTOR_NETWORKS}/:actorId`,
  ACTOR_NETWORKS_DATABASE_SELLER_DETAILS: `${BASE_URL}/${ROUTE_PATHS.DATABASE}/${ROUTE_PATHS.SELLER_DETAIL}/:sellerId/${ROUTE_PATHS.ACTOR_NETWORKS}/:actorId`,
  ACTOR_NETWORKS_VALIDATION: `${BASE_URL}/${ROUTE_PATHS.VALIDATION}/${ROUTE_PATHS.ACTOR_NETWORKS}/:actorId`,
  ACTOR_NETWORKS_VALIDATION_ACCOUNT_DETAILS: `${BASE_URL}/${ROUTE_PATHS.VALIDATION}/${ROUTE_PATHS.ACCOUNT_DETAIL}/:accountId/${ROUTE_PATHS.ACTOR_NETWORKS}/:actorId`,
  ACTOR_NETWORKS_VALIDATION_INCIDENT_DETAILS_ACCOUNT_DETAILS: `${BASE_URL}/${ROUTE_PATHS.VALIDATION}/:incidentId/${ROUTE_PATHS.ACCOUNT_DETAIL}/:accountId/${ROUTE_PATHS.ACTOR_NETWORKS}/:actorId`,
  ACTOR_NETWORKS_VALIDATION_INCIDENT_DETAILS_SELLER_DETAILS: `${BASE_URL}/${ROUTE_PATHS.VALIDATION}/:incidentId/${ROUTE_PATHS.SELLER_DETAIL}/:sellerId/${ROUTE_PATHS.ACTOR_NETWORKS}/:actorId`,
  ACTOR_NETWORKS_VALIDATION_SELLER_DETAILS: `${BASE_URL}/${ROUTE_PATHS.VALIDATION}/${ROUTE_PATHS.SELLER_DETAIL}/:sellerId/${ROUTE_PATHS.ACTOR_NETWORKS}/:actorId`,
  ACTORS: `${BASE_URL}/${ROUTE_PATHS.ACTORS}`,
  NETWORKS: `${BASE_URL}/${ROUTE_PATHS.NETWORKS}`,
  ANTIPIRACY_SUMMARY: `${BASE_URL}/${ROUTE_PATHS.ANTIPIRACY_SUMMARY}`,
  BASE: BASE_URL,
  BACKOFFICE_SEARCH_KEYWORDS_LIST_BY_ASSET: `${BASE_URL}/${ROUTE_PATHS.BACKOFFICE_SEARCH_KEYWORDS_LIST_BY_ASSET}`,
  TAGS_MANAGEMENT: `${BASE_URL}/${ROUTE_PATHS.TAGS_MANAGEMENT}`,
  CUSTOM_REPORTS: `${BASE_URL}/${ROUTE_PATHS.CUSTOM_REPORTS}`,
  DATABASE: `${BASE_URL}/${ROUTE_PATHS.DATABASE}`,
  DOCUMENT_CREATE: `${BASE_URL}/${ROUTE_PATHS.DOCUMENTS}/${ROUTE_PATHS.DOCUMENT_CREATE}`,
  DOCUMENTS_DETAILS: `${BASE_URL}/${ROUTE_PATHS.DOCUMENTS}/${ROUTE_PATHS.DOCUMENTS_DETAILS}`,
  DOCUMENTS_EDITION: `${BASE_URL}/${ROUTE_PATHS.DOCUMENTS}/${ROUTE_PATHS.DOCUMENTS_EDITION}`,
  DOCUMENTS: `${BASE_URL}/${ROUTE_PATHS.DOCUMENTS}`,
  DOMAIN_MANAGEMENT: `${BASE_URL}/${ROUTE_PATHS.DOMAIN_MANAGEMENT}`,
  ECONOMIC_IMPACT: `${BASE_URL}/${ROUTE_PATHS.ECONOMIC_IMPACT}`,
  ENFORCEMENT_AUDIT: `${BASE_URL}/${ROUTE_PATHS.ENFORCEMENT_AUDIT}`,
  HOME: `${BASE_URL}/${ROUTE_PATHS.HOME}`,
  INCIDENT_DETAILS_DATABASE: `${BASE_URL}/${ROUTE_PATHS.INCIDENT_DETAILS_DATABASE}`,
  INCIDENT_DETAILS_VALIDATION: `${BASE_URL}/${ROUTE_PATHS.INCIDENT_DETAILS_VALIDATION}`,
  IPR_RULES_MANAGEMENT_INCIDENT: `${BASE_URL}/${ROUTE_PATHS.IPR_RULES_MANAGEMENT_INCIDENT}`,
  IP_OFFLINE_SERVICES: `${BASE_URL}/${ROUTE_PATHS.IP_OFFLINE_SERVICES}`,
  JOURNEY: `${BASE_URL}/${ROUTE_PATHS.JOURNEY}`,
  MY_LINKS_AND_DOCS: `${BASE_URL}/${ROUTE_PATHS.MY_LINKS_AND_DOCS}`,
  NEW_INCIDENT: `${BASE_URL}/${ROUTE_PATHS.NEW_INCIDENT}`,
  ASSET_VERIFICATION: `${BASE_URL}/${ROUTE_PATHS.ASSET_VERIFICATION}`,
  ASSET_VERIFICATION_CARD_DETAIL: `${BASE_URL}/${ROUTE_PATHS.ASSET_VERIFICATION_CARD_DETAIL}`,
  INFRINGEMENT_EVALUATION: `${BASE_URL}/${ROUTE_PATHS.INFRINGEMENT_EVALUATION}`,
  NEW_SELLER_MANAGEMENT: `${BASE_URL}/${ROUTE_PATHS.NEW_SELLER_MANAGEMENT}`,
  NON_ENFORCEABLE: `${BASE_URL}/${ROUTE_PATHS.NON_ENFORCEABLE}`,
  RESULT_ANALYSIS: `${BASE_URL}/${ROUTE_PATHS.RESULT_ANALYSIS}`,
  REVENUE_RECOVERY: `${BASE_URL}/${ROUTE_PATHS.REVENUE_RECOVERY}`,
  ROOT: '/',
  RULE_SUGGESTIONS: `${BASE_URL}/${ROUTE_PATHS.RULE_SUGGESTIONS}`,
  SELLER_DETAIL: `${BASE_URL}/${ROUTE_PATHS.SELLER_DETAIL}`,
  SELLER_DETAILS_ACTORS: `${BASE_URL}/${ROUTE_PATHS.SELLER_DETAILS_ACTORS}`,
  SELLER_DETAILS_DATABASE: `${BASE_URL}/${ROUTE_PATHS.SELLER_DETAILS_DATABASE}`,
  SELLER_DETAILS_DATABASE_INCIDENT_DETAILS: `${BASE_URL}/${ROUTE_PATHS.SELLER_DETAILS_DATABASE_INCIDENT_DETAILS}`,
  SELLER_DETAILS_VALIDATION: `${BASE_URL}/${ROUTE_PATHS.SELLER_DETAILS_VALIDATION}`,
  SELLER_DETAILS_VALIDATION_INCIDENT_DETAILS: `${BASE_URL}/${ROUTE_PATHS.SELLER_DETAILS_VALIDATION_INCIDENT_DETAILS}`,
  SELLER_GROUP_DETAILS_DATABASE: `${BASE_URL}/${ROUTE_PATHS.SELLER_GROUP_DETAILS_DATABASE}`,
  SELLER_GROUP_DETAILS_VALIDATION: `${BASE_URL}/${ROUTE_PATHS.SELLER_GROUP_DETAILS_VALIDATION}`,
  SELLER_MANAGEMENT: `${BASE_URL}/${ROUTE_PATHS.SELLER_MANAGEMENT}`,
  SELLERS: `${BASE_URL}/${ROUTE_PATHS.SELLERS}`,
  SMART_ONBOARDING: `${BASE_URL}/${ROUTE_PATHS.SMART_ONBOARDING}`,
  SOCIAL_MEDIA_IMPACT: `${BASE_URL}/${ROUTE_PATHS.SOCIAL_MEDIA_IMPACT}`,
  SUCCESS_PLANS: `${BASE_URL}/${ROUTE_PATHS.SUCCESS_PLANS}`,
  SUMMARY: `${BASE_URL}/${ROUTE_PATHS.SUMMARY}`,
  TEMPLATES: `${BASE_URL}/${ROUTE_PATHS.TEMPLATES}`,
  TEMPLATES_CREATE: `${BASE_URL}/${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.TEMPLATES_CREATE}`,
  TEMPLATES_EDIT: `${BASE_URL}/${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.TEMPLATES_EDIT}`,
  VALIDATION: `${BASE_URL}/${ROUTE_PATHS.VALIDATION}`,
  WEBSITE_INFRINGEMENTS: `${BASE_URL}/${ROUTE_PATHS.WEBSITE_INFRINGEMENTS}`,
} as const;
